/**
 * SequencerClock - Web Audio-based timing engine for step sequencer
 *
 * Uses lookahead scheduling pattern for sample-accurate timing.
 * Schedules steps ahead of time using AudioContext.currentTime.
 */

const VALID_STEP_COUNTS = [8, 16, 32];

export class SequencerClock {
  constructor(options) {
    if (!options?.audioContext) {
      throw new Error('SequencerClock requires an audioContext option');
    }
    this.audioContext = options.audioContext;
    this.onStep = options.onStep || (() => {});

    // Timing configuration
    this.scheduleAheadTime = 0.1;  // Schedule 100ms ahead
    this.lookAhead = 25;           // Check every 25ms

    // State
    this.isPlaying = false;
    this.currentStep = 0;
    this.stepCount = 16;
    this.bpm = 120;
    this.swing = 0;  // 0-100%

    // Internal timing
    this.nextStepTime = 0;
    this.timerID = null;
  }

  /**
   * Calculate time per step in seconds
   */
  getStepDuration() {
    // 16th notes: 4 steps per beat
    return 60 / this.bpm / 4;
  }

  /**
   * Calculate swing offset for a given step
   */
  getSwingOffset(stepIndex) {
    // Only swing off-beats (odd indices)
    if (stepIndex % 2 === 0) return 0;
    return (this.swing / 100) * this.getStepDuration() * 0.5;
  }

  /**
   * Schedule step and advance
   */
  scheduleStep(stepIndex, time) {
    this.onStep(stepIndex, time);
  }

  /**
   * Advance to next step
   */
  advanceStep() {
    const stepDuration = this.getStepDuration();
    const swingOffset = this.getSwingOffset(this.currentStep);

    this.nextStepTime += stepDuration + swingOffset;
    this.currentStep = (this.currentStep + 1) % this.stepCount;
  }

  /**
   * Main scheduler tick - called by timer
   */
  tick() {
    while (this.nextStepTime < this.audioContext.currentTime + this.scheduleAheadTime) {
      this.scheduleStep(this.currentStep, this.nextStepTime);
      this.advanceStep();
    }
  }

  /**
   * Start playback
   */
  start() {
    // Clear any existing timer to prevent orphaned timers
    if (this.timerID) {
      clearInterval(this.timerID);
      this.timerID = null;
    }

    if (this.isPlaying) return;

    this.isPlaying = true;
    this.currentStep = 0;
    this.nextStepTime = this.audioContext.currentTime + 0.05; // Small delay to sync

    this.tick();
    this.timerID = setInterval(() => this.tick(), this.lookAhead);
  }

  /**
   * Stop playback
   */
  stop() {
    if (!this.isPlaying) return;

    this.isPlaying = false;
    this.currentStep = 0;

    if (this.timerID) {
      clearInterval(this.timerID);
      this.timerID = null;
    }
  }

  /**
   * Toggle play/stop
   */
  toggle() {
    if (this.isPlaying) {
      this.stop();
    } else {
      this.start();
    }
  }

  /**
   * Set BPM
   */
  setBpm(bpm) {
    this.bpm = Math.max(60, Math.min(200, bpm));
  }

  /**
   * Set swing amount (0-100)
   */
  setSwing(swing) {
    this.swing = Math.max(0, Math.min(100, swing));
  }

  /**
   * Set step count (8, 16, or 32)
   */
  setStepCount(count) {
    // Find closest valid step count
    const validCount = VALID_STEP_COUNTS.reduce((prev, curr) =>
      Math.abs(curr - count) < Math.abs(prev - count) ? curr : prev
    );
    this.stepCount = validCount;
    if (this.currentStep >= validCount) {
      this.currentStep = 0;
    }
  }

  /**
   * Get current step (for visual sync)
   */
  getCurrentStep() {
    return this.currentStep;
  }

  /**
   * Check if playing
   */
  getIsPlaying() {
    return this.isPlaying;
  }

  /**
   * Cleanup
   */
  destroy() {
    this.stop();
  }
}
